/*
 * סוגי קרקע.
 *
 * סוג קרקע חדש = רשומה אחת כאן. המנוע מפעיל לפי אינדקס (`TerrainId`), ואף מערכת
 * לא מכירה "מים" או "סלע" בשמם — היא שואלת `passable` ו-`speedPct`.
 *
 * `char` הוא הייצוג בקובצי המפה, שנכתבות כשורות טקסט קריאות. `ore` אינו סוג קרקע
 * אלא שכבה נפרדת: התו 'o' במפה מציב חול ועליו כמות עפרות התחלתית.
 */

export interface TerrainDef {
    readonly id: string;
    /** התו בקובץ המפה. */
    readonly char: string;
    readonly passable: boolean;
    /** אחוז מהמהירות הבסיסית. 100 = מלא. חסר משמעות כשאינו עביר. */
    readonly speedPct: number;
    readonly buildable: boolean;
    /** צבע בסיס. הרנדרר מוסיף שונות דטרמיניסטית לפי מיקום האריח. */
    readonly color: string;
    /** עוצמת השונות הדטרמיניסטית, ביחידות בהירות 0..255. */
    readonly colorJitter: number;
}

export const TERRAIN_DEFS = [
    {
        id: 'grass',
        char: '.',
        passable: true,
        speedPct: 100,
        buildable: true,
        color: '#2c3b22',
        colorJitter: 10,
    },
    {
        id: 'sand',
        char: ',',
        passable: true,
        speedPct: 90,
        buildable: true,
        color: '#4a4230',
        colorJitter: 9,
    },
    {
        id: 'water',
        char: '~',
        passable: false,
        speedPct: 0,
        buildable: false,
        color: '#1c3340',
        colorJitter: 7,
    },
    {
        id: 'rock',
        char: '#',
        passable: false,
        speedPct: 0,
        buildable: false,
        color: '#3a3a3d',
        colorJitter: 12,
    },
] as const satisfies readonly TerrainDef[];

export type TerrainName = (typeof TERRAIN_DEFS)[number]['id'];

/** אינדקס בתוך TERRAIN_DEFS. זה מה שנשמר במערך הקרקע של המצב. */
export type TerrainId = number;

export const TERRAIN_BY_NAME: Readonly<Record<TerrainName, TerrainId>> = Object.fromEntries(
    TERRAIN_DEFS.map((def, index) => [def.id, index]),
) as Record<TerrainName, TerrainId>;

/** התו במפה שמסמן עפרות. מציב חול ומעליו כמות התחלתית. */
export const ORE_CHAR = 'o';

/** כמות העפרות ההתחלתית באריח שסומן ב-ORE_CHAR. */
export const ORE_INITIAL_AMOUNT = 600;

const CHAR_TO_TERRAIN = new Map<string, TerrainId>(
    TERRAIN_DEFS.map((def, index) => [def.char, index]),
);
CHAR_TO_TERRAIN.set(ORE_CHAR, TERRAIN_BY_NAME.sand);

export function terrainFromChar(char: string): TerrainId | undefined {
    return CHAR_TO_TERRAIN.get(char);
}

export function terrainDef(id: TerrainId): TerrainDef {
    const def = TERRAIN_DEFS[id];
    if (!def) throw new Error(`סוג קרקע לא מוכר: ${id}`);
    return def;
}

export function isPassable(id: TerrainId): boolean {
    return terrainDef(id).passable;
}
